// src/hooks/useCart.js
import { useState, useEffect, useCallback } from 'react';
import { calculateTotalWithFee, calculateCCFee } from '../config/constants';

const CART_STORAGE_KEY = 'wholesale_cart';

export function useCart() {
  const [cart, setCart] = useState(() => {
    // Initialize from localStorage
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product, quantity) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    setCart(prev => {
      if (quantity <= 0) {
        return prev.filter(item => item.id !== productId);
      }
      return prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // Calculate totals
  const subtotal = cart.reduce(
    (sum, item) => sum + item.wholesalePrice * item.quantity,
    0
  );
  
  const ccFee = calculateCCFee(subtotal);
  const totalWithFee = calculateTotalWithFee(subtotal);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return {
    cart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    subtotal: Math.round(subtotal * 100) / 100,
    ccFee,
    totalWithFee,
    itemCount
  };
}
